import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionService } from '../services/session.service';
import { GlobalAlertService } from '../services/global-alert.service';

// Auth endpoints that should NOT trigger the "Session Expired" dialog on 401.
// These are unauthenticated routes where a 401 means "wrong credentials", not "session expired".
const AUTH_ROUTE_PATTERNS = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];

const isAuthRoute = (url: string): boolean =>
  AUTH_ROUTE_PATTERNS.some(pattern => url.toLowerCase().includes(pattern));

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  const alertService = inject(GlobalAlertService);
  const router = inject(Router);
  const token = sessionService.token();

  const handleAuthError = (err: any) => {
    // Only show "Session Expired" for 401s that come from authenticated (non-auth) endpoints.
    // Login/signup endpoints return 401 for wrong credentials — let those bubble up normally.
    if (err instanceof HttpErrorResponse && err.status === 401 && !isAuthRoute(req.url)) {
      alertService.showError('Session timeout. Please login again.', 'Session Expired');
      sessionService.clearSession();
      router.navigate(['/auth/login']);
    }
    return throwError(() => err);
  };

  if (!token) {
    return next(req).pipe(catchError(handleAuthError));
  }

  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  return next(authReq).pipe(catchError(handleAuthError));
};